import { z } from "zod";

import { bundleComponentSchema } from "./create-bundle-schema";

/**
 * A bundle component row as edited in the form. Extends the API component shape
 * (`base_sino_sku`, `multiplicity`) with a display-only `product_name` so the
 * picker can show the human-readable name next to the read-only SKU. The
 * `product_name` is dropped when mapping to the API request body.
 */
export const componentRowSchema = bundleComponentSchema.extend({
  product_name: z.string().optional(),
});

/**
 * Single schema for the unified Create SKU/Bundle modal. `is_bundle` is the
 * discriminator:
 *  - false → plain base SKU (components ignored)
 *  - true  → bundle (requires ≥1 component, each multiplicity an int ≥ 1)
 * `code` doubles as `sino_sku` (SKU) or `listing_code` (bundle).
 * No gift-stock pool flag: sino has no such concept (gift = base-derived).
 */
export const createSkuBundleSchema = z
  .object({
    is_bundle: z.boolean(),
    code: z.string().min(1, "Required").max(128, "Max 128 characters"),
    product_name: z.string().min(1, "Required").max(255, "Max 255 characters"),
    components: z.array(componentRowSchema),
    /** Optional "Brand (AP)" (spec §3.4). Empty = unmapped; BE normalises (trim/UPPER). */
    ap_name: z.string().max(128, "Max 128 characters").optional(),
  })
  .superRefine((value, ctx) => {
    if (value.is_bundle && value.components.length === 0) {
      ctx.addIssue({
        code: "custom",
        message: "At least one component is required",
        path: ["components"],
      });
    }
  });

export type CreateSkuBundleFormValues = z.infer<typeof createSkuBundleSchema>;
export type CreateSkuBundleFormInput = z.input<typeof createSkuBundleSchema>;
export type ComponentRow = z.infer<typeof componentRowSchema>;
